import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Search, AlertCircle } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import pyqService from '../services/pyqService'
import { usePyqExams } from '../hooks/usePYQ'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { trackPYQBrowseStarted } from '@/shared/utils/analytics'

// Syllabus subject node codes accepted by /questions/pyq/by-subject
const SUBJECT_OPTIONS = [
  { nodeCode: 'NEET_UG_PHY', label: 'Physics' },
  { nodeCode: 'NEET_UG_CHEM', label: 'Chemistry' },
  { nodeCode: 'NEET_UG_BIO', label: 'Biology' },
]

export default function PYQByExamPage() {
  const navigate = useNavigate()
  const [pickedExam, setPickedExam] = useState('')
  const [selectedSubject, setSelectedSubject] = useState(SUBJECT_OPTIONS[0].nodeCode)
  const { data: examYears, isLoading: isLoadingExams, isError: isExamsError, refetch: refetchExams } = usePyqExams()

  const examLabels = useMemo(
    () => new Map((examYears ?? []).flatMap((y) => y.exams.map((e) => [e.testCode, e.label] as const))),
    [examYears]
  )

  // Default to the first (newest) exam until the user picks one
  const selectedExam = pickedExam || examYears?.[0]?.exams[0]?.testCode || ''

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('pyq_by_exam', undefined, 'Browse PYQ by Exam')
  }, [])

  const searchMutation = useMutation({
    mutationFn: ({ testCode, subject }: { testCode: string; subject: string }) =>
      pyqService.searchPyqBySubject(testCode, subject),
    onSuccess: (response, { testCode, subject }) => {
      const questions = response?.content || []
      const subjectLabel = SUBJECT_OPTIONS.find((s) => s.nodeCode === subject)?.label
      const examLabel = [examLabels.get(testCode) || testCode, subjectLabel].filter(Boolean).join(' · ')

      // Track PYQ browse started
      trackPYQBrowseStarted('year')

      // Navigate to full-page exam browser with questions
      navigate('/question-bank/exam-browser', {
        state: {
          questions,
          testCode,
          examLabel,
        }
      })
    },
  })

  const handleFetchQuestions = () => {
    if (!selectedExam || !selectedSubject) return
    searchMutation.mutate({ testCode: selectedExam, subject: selectedSubject })
  }

  return (
    <div>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Browse PYQ by Exam</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          Select an exam and a subject to view its questions.
        </p>
      </div>

      {/* Search Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="exam-select" className="block text-sm font-medium text-gray-700 mb-1">
              Select Exam
            </label>
            <select
              id="exam-select"
              value={selectedExam}
              onChange={(e) => setPickedExam(e.target.value)}
              disabled={isLoadingExams || isExamsError}
              className="w-full disabled:bg-gray-100 disabled:cursor-not-allowed px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {!examYears?.length && (
                <option value="">{isLoadingExams ? 'Loading exams...' : 'No exams available'}</option>
              )}
              {examYears?.map((year) => (
                <optgroup key={year.year} label={String(year.year)}>
                  {year.exams.map((exam) => (
                    <option key={exam.testCode} value={exam.testCode}>
                      {exam.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {isExamsError && (
              <p className="mt-2 text-sm text-red-600">
                Couldn't load the exam list.{' '}
                <button onClick={() => refetchExams()} className="font-medium underline">
                  Retry
                </button>
              </p>
            )}
          </div>
          <div className="sm:w-48">
            <label htmlFor="subject-select" className="block text-sm font-medium text-gray-700 mb-1">
              Select Subject
            </label>
            <select
              id="subject-select"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {SUBJECT_OPTIONS.map((subject) => (
                <option key={subject.nodeCode} value={subject.nodeCode}>
                  {subject.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex sm:pt-6">
            <button
              onClick={handleFetchQuestions}
              disabled={!selectedExam || !selectedSubject || searchMutation.isPending}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {searchMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Browse Questions
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {searchMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800">Failed to fetch questions</h3>
              <p className="text-sm text-red-600 mt-1">
                Please try again later or select a different exam or subject.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Initial State */}
      {!searchMutation.isPending && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 sm:p-8 text-center">
          <Search className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="font-medium text-gray-700">Select an exam and subject to get started</h3>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Choose an exam and a subject above and click "Browse Questions" to view its questions.
          </p>
        </div>
      )}
    </div>
  )
}
